<template>
  <div class="event-sidebar">
    <section v-if="showMap">
      <aba-map
        ref="map"
        :center="latLng || { lat: 52.5220676, lng: 13.4121466 }"
        :markers="mapMarkers"
        class="mb-base"/>
    </section>
    <section>
      <profile-cell
        v-for="profile in profiles"
        :key="profile.id"
        :profile="profile"
        class="mb-base"/>
    </section>
    <section v-if="authorEvents.length">
      <div v-for="event in authorEvents" :key="event.id">
        <post-cell :post="event" />
      </div>
    </section>
    <smooth-reflow>
      <section v-if="tags">
        <div v-for="tag in tags" :key="tag.id" class="bg-aba-blue text-white">{{tag.title}}</div>
        <post-cell v-for="tagPost in tagPosts" :key="tagPost.id" :post="tagPost" />
      </section>
    </smooth-reflow>
  </div>
</template>

<script>
import { db, FieldPath } from '../lib/firebase'
import PostCell from './components/PostCell'
import SmoothReflow from './components/UI/SmoothReflow'
import ProfileCell from './components/ProfileCell'
import { abaMapStyle } from '../lib/map'
import AbaMap from './components/UI/AbaMap'

export default {
  name: 'EventSidebar',
  components: { AbaMap, ProfileCell, SmoothReflow, PostCell },
  props: {
    post: { type: Object, default: () => ({}) },
    showMap: Boolean
  },

  data: () => ({
    profiles: [],
    authorEvents: [],
    tagPosts: [],
    profileUnsubscribe: null,
    mapOptions: {
      disableDefaultUI: true,
      backgroundColor: '#fff',
      styles: [...abaMapStyle.basic]
    }
  }),

  computed: {
    persons () {
      if (!(this.post || {}).participants) return []
      return this.post.participants
    },

    tags () {
      return (this.post || {}).tags || []
    },

    participantsIds () {
      return ((this.post || {}).participants || []).map(p => p.id)
    },

    latLng () {
      if (!(this.post || {}).location) return null
      const { lat, lng } = this.post.location
      // !!! DEBUG !!!
      console.log(`%c latLng() %c lat, lng: `, 'background:#ffbb00;color:#000', 'color:#00aaff', lat, lng)
      return { lat: parseFloat(lat), lng: parseFloat(lng) }
    },

    mapMarkers () {
      if (!this.latLng) {
        console.log('EventSidebar: No latLng available for map') // Debug log
        return []
      }
      const markers = [{
        ...this.latLng,
        label: `#${this.post.countNumber}`,
        title: this.post.title || 'Event Location',
        description: this.post.excerpt || '',
        url: this.post.url || '',
        id: this.post.id,
        token: this.post.token || this.post.id,
        active: false
      }]
      console.log('EventSidebar: Generated map markers:', markers) // Debug log
      return markers
    }
  },

  created () {
    console.log('EventSidebar created, post:', this.post) // Debug log
    console.log('EventSidebar showMap prop:', this.showMap) // Debug log
    this.subscribeParticipantsProfiles()
    this.getRelatedEvents()
    this.getTagPosts()
  },

  mounted () {
    console.log('EventSidebar mounted, latLng:', this.latLng) // Debug log
    console.log('EventSidebar mapMarkers:', this.mapMarkers) // Debug log
  },

  watch: {
    post () {
      console.log('EventSidebar post changed:', this.post) // Debug log
      console.log('EventSidebar new latLng:', this.latLng) // Debug log
      console.log('EventSidebar new mapMarkers:', this.mapMarkers) // Debug log
      this.subscribeParticipantsProfiles()
      this.getRelatedEvents()
      this.getTagPosts()
    }
  },

  beforeDestroy () {
    if (typeof this.profileUnsubscribe === 'function') {
      this.profileUnsubscribe()
    }
  },

  methods: {
    subscribeParticipantsProfiles () {
      this.profiles = []
      if (!this.participantsIds.length) return
      db.collection('profiles')
        .where(FieldPath.documentId(), 'in', this.participantsIds)
        .get()
        .then(snapshot => {
          snapshot.forEach(doc => {
            this.profiles.push({ ...doc.data(), id: doc.id })
          })
        })
    },

    getRelatedEvents () {
      this.authorEvents = []
      const participants = (this.post || {}).participants || []
      if (!participants.length) {
        return
      }
      // Get events that share at least one participant with this event
      // Firestore doesn't support array-contains-any with objects, so we need to query for each participant
      const queries = participants.slice(0, 10).map(participant => {
        return db.collection('posts')
          .where('type', '==', 'event')
          .where('status', '==', 'public')
          .where('participants', 'array-contains', participant)
          .get()
      })
      Promise.all(queries)
        .then(snapshots => {
          const eventMap = new Map()
          snapshots.forEach(snapshot => {
            snapshot.forEach(doc => {
              const eventData = { ...doc.data(), id: doc.id }
              // Exclude the current event
              if (eventData.id !== (this.post || {}).id) {
                eventMap.set(doc.id, eventData)
              }
            })
          })
          // Also check for events in the same programme if this event is part of a programme
          if ((this.post || {}).partOfProgramme && (this.post || {}).partOfProgramme.programmeId) {
            return db.collection('posts')
              .where('type', '==', 'event')
              .where('status', '==', 'public')
              .where('partOfProgramme.programmeId', '==', this.post.partOfProgramme.programmeId)
              .get()
              .then(programmeSnapshot => {
                programmeSnapshot.forEach(doc => {
                  const eventData = { ...doc.data(), id: doc.id }
                  if (eventData.id !== (this.post || {}).id) {
                    eventMap.set(doc.id, eventData)
                  }
                })
                return Array.from(eventMap.values())
              })
          }
          return Array.from(eventMap.values())
        })
        .then(events => {
          // Sort by date (most recent first)
          this.authorEvents = events.sort((a, b) => {
            const dateA = a.date || 0
            const dateB = b.date || 0
            return dateB - dateA
          })
        })
        .catch(err => {
          console.error('Error fetching related events:', err)
        })
    },
    getTagPosts () {
      const tagIds = (this.post || {}).tagIds || []
      if (!tagIds.length) {
        this.tagPosts = []
        return
      }
      db.collection('posts')
        .where('status', '==', 'public')
        .where('tagIds', 'array-contains-any', tagIds)
        .get()
        .then(snapshot => {
          const posts = []
          snapshot.forEach(doc => {
            posts.push({ ...doc.data(), id: doc.id })
          })
          return posts
        })
        .then(posts => {
          this.tagPosts = posts.filter(post => {
            // Exclude the current event
            return post.id !== (this.post || {}).id && post.type === 'event'
          })
        })
    },

    stopEventPropagation: function () {
      var anchor = this.anchor
      anchor.style.cursor = 'auto';
      ['click', 'dblclick', 'contextmenu', 'wheel', 'mousedown', 'touchstart',
        'pointerdown']
        .forEach(function (event) {
          anchor.addEventListener(event, function (e) {
            e.stopPropagation()
          })
        })
    }
  }
}
</script>

<style lang="scss">
  @import "../styles/mixins";
  .excerpt {
    @include multi-line-truncate(3);
  }
</style>
